import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Loader2, Copy, Trash2, FileText, Film } from "lucide-react";
import { toast } from "sonner";
import { PageTitle, uploadFile } from "./AdminKit";
import { api, fileUrl, formatErr } from "../../lib/api";

const Thumb = ({ m }) => {
  if (m.content_type.startsWith("image/")) return <img src={fileUrl(m.url)} alt="" loading="lazy" className="h-full w-full object-cover" />;
  const Icon = m.content_type === "application/pdf" ? FileText : Film;
  return <span className="flex h-full items-center justify-center"><Icon className="h-10 w-10 text-slate-400" /></span>;
};

export const MediaAdmin = () => {
  const qc = useQueryClient();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["admin-media"], queryFn: () => api.get("/admin/media").then((r) => r.data) });
  const items = Array.isArray(data) ? data : [];

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      try { await uploadFile(f); toast.success(`Uploaded ${f.name}`); } catch (err) { toast.error(`${f.name}: ${formatErr(err)}`); }
    }
    setBusy(false);
    e.target.value = "";
    qc.invalidateQueries({ queryKey: ["admin-media"] });
  };
  const copy = (m) => { navigator.clipboard?.writeText(m.url); toast.success("Link copied — paste it into any image, video or PDF field"); };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.original_filename}?`)) return;
    await api.delete(`/admin/media/${m.id}`); qc.invalidateQueries({ queryKey: ["admin-media"] });
  };

  return (
    <div>
      <PageTitle title="Media library" desc="Upload photos, videos and PDFs (up to 80 MB each). Copy a link to use it anywhere.">
        <input ref={ref} type="file" multiple accept="image/*,video/mp4,video/webm,application/pdf" className="hidden" onChange={pick} data-testid="admin-media-file" />
        <button data-testid="admin-media-upload-button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 bg-[#0a6cff] px-4 py-2 text-xs font-semibold text-white disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Upload files
        </button>
      </PageTitle>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {items.map((m) => (
          <div key={m.id} data-testid={`admin-media-item-${m.id.slice(0, 8)}`} className="bg-white">
            <div className="aspect-square bg-slate-100"><Thumb m={m} /></div>
            <div className="flex items-center gap-1 p-2">
              <p className="min-w-0 flex-1 truncate text-xs text-slate-700">{m.original_filename}</p>
              <button onClick={() => copy(m)} className="p-1.5 text-slate-500 hover:text-[#0a6cff]" aria-label="Copy link"><Copy className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(m)} className="p-1.5 text-slate-500 hover:text-red-500" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && <p className="bg-white p-8 text-sm text-slate-500">No files yet. Upload product photos, factory videos or your PDFs.</p>}
    </div>
  );
};
